/* eslint-disable @typescript-eslint/naming-convention */

// internal-deps
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// interfaces
import {
    GetGroupCartOrderResponse,
    GroupOrderRestaurantDetails,
    IUpdateGroupCartOrderRequestPayload,
    UserSpecificDetails,
    UsersOrderMap,
    CartItem,
    UpdateGroupCartOrderResponse,
    GroupOrderError,
    GroupCartItemRendererProps,
    CART_ERROR_CTA,
    ApiMenuCartItem,
} from "@swiggy-private/cart-types";

// constants
import { CART_STATUS_CODES, GROUP_CART_HAS_LOCKED_TEXT, GROUP_CART_STATUS_CODES } from "@swiggy-private/cart-ui";

// helpers
import { getAvailableCartItems, navigateToHomePage } from "./Cart";
import { convertToTitleCase } from "@swiggy-private/core-ui";

export const transformGroupCartResponseToCartItemsListProps = (
    userDetails: UserSpecificDetails,
    restaurantDetails: GroupOrderRestaurantDetails,
    groupOrderProps: {
        isGroupOrderFlow: boolean;
        isHost: boolean;
        isCurrentItemFromSameUser: boolean;
        groupCartId: string;
    },
): Omit<GroupCartItemRendererProps, "updateGroupCart"> => {
    return {
        cartItems: userDetails.user_cart,
        groupTagDetails: [
            {
                group_tag_id: "",
                group_tag_type: "",
                group_tag_sub_id: "",
            },
        ],
        mealItems: [],
        restaurantDetails: {
            address: "",
            area_name: "",
            availability: "",
            bgColor: "",
            city: "",
            cloudinary_image_id: "",
            id: restaurantDetails.restaurant_id,
            lat: "",
            lng: "",
            name: restaurantDetails.restaurant_name,
            phoneNo: "",
            sla: { slaString: "" },
            slug: "",
            special_instructions_opted: false,
            restaurant_special_instruction: "",
        },
        showInputQtyField: false,
        currentUserId: userDetails.user_details.user_id,
        couponDetails: undefined,
        cartMenuItemsHeaderList: undefined,
        ...groupOrderProps,
    };
};

export const isCurrentUserHost = (hostUserId?: string): boolean => {
    const currentId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);
    return currentId === hostUserId;
};

export const getHostFirstName = (response?: GetGroupCartOrderResponse["data"]): string | undefined => {
    if (!response) return "";

    const hostUserId = response.host_user_id;

    const rightUser = Object.values(response?.user_cart_map).find(user => user.user_details.user_id === hostUserId);

    return rightUser?.user_details.user_first_name;
};

export const getTotalPeopleInGroup = (response: GetGroupCartOrderResponse["data"]): number => {
    return Object.keys(response?.user_cart_map).length;
};

export const formUpdateOrderCartRequestPayload = (
    cartItems: ApiMenuCartItem[],
    userId: string,
    state?: GetGroupCartOrderResponse["data"] | UpdateGroupCartOrderResponse["data"],
    groupCartId?: string,
): IUpdateGroupCartOrderRequestPayload => {
    // skip heavy and unnecessary fields like valid_addons, valid_variants, valid_variants_v2
    // which can lead to HTTP431 errors
    const updatedCartItems = cartItems.map(cartItem => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { valid_addons, valid_variants, valid_variants_v2, ...rest } = cartItem;
        return rest;
    }) as ApiMenuCartItem[];

    return {
        restaurant_id: state?.restaurant_details.restaurant_id || "",
        address_id: state?.address_id || "",
        group_cart_id: groupCartId || "",
        host_user_id: state?.host_user_id || "",
        user_id: userId || "",
        cartItems: updatedCartItems,
        mealItems: [],
    };
};

export const isCurrentItemFromSameUser = (userId: string): boolean =>
    GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId) === userId;

export const sortUsersList = (usersMap: UsersOrderMap): UserSpecificDetails[] => {
    const usersList = Object.values(usersMap);

    const currentUserCartDetails = usersList.find(user =>
        isCurrentItemFromSameUser(user.user_details.user_id),
    ) as UserSpecificDetails;

    const filteredUsersList = usersList
        .filter(user => !isCurrentItemFromSameUser(user.user_details.user_id))
        .sort((userOne, userTwo) =>
            userOne.user_details.user_first_name.localeCompare(userTwo.user_details.user_first_name),
        );

    const finalList = isEmpty(currentUserCartDetails)
        ? [...filteredUsersList]
        : [{ ...currentUserCartDetails }, ...filteredUsersList];

    return finalList;
};

export const getUserFullName = (userDetails: UserSpecificDetails): string => {
    const _userDetails = userDetails.user_details;
    let userFullName = "";
    if (!isEmpty(_userDetails.user_first_name)) userFullName += _userDetails.user_first_name;
    if (!isEmpty(_userDetails.user_last_name)) userFullName += " " + _userDetails.user_last_name;
    return convertToTitleCase(userFullName);
};

export const getGroupOrderErrorPayload = (
    response: GetGroupCartOrderResponse | UpdateGroupCartOrderResponse,
): GroupOrderError => {
    if (response.statusCode !== CART_STATUS_CODES.SUCCESS) {
        return {
            error: true,
            statusCode: response.statusCode,
            titleMessage: response.statusTitle || undefined,
            statusMessage: response.statusMessage,
            ctaText: response.ctaText || undefined,
            ctaAction: response.ctaAction || undefined,
        };
    }
    return {
        error: false,
    };
};

export const isCurrentGroupCartLocked = (statusCode: number): boolean => {
    return statusCode === GROUP_CART_STATUS_CODES.GROUP_CART_HAS_LOCKED;
};

export const isCurrentGroupOrderPlaced = (statusCode?: number): boolean => {
    return statusCode === GROUP_CART_STATUS_CODES.GROUP_CART_ORDER_PLACED;
};

export const isCurrentGroupOrderDeleted = (statusCode?: number): boolean => {
    return statusCode === GROUP_CART_STATUS_CODES.GROUP_CART_NO_LONGER_EXISTS;
};

export const getLockedGroupCartErrorHandling = (
    groupCartOrderData: GetGroupCartOrderResponse | UpdateGroupCartOrderResponse,
): string | undefined => {
    return isCurrentGroupCartLocked(groupCartOrderData.statusCode) ? GROUP_CART_HAS_LOCKED_TEXT : undefined;
};

export const onClickGroupCartErrorAction = (
    action: string,
    isHost: boolean,
    updateGroupCart: (userId: string, updatedUserCart: CartItem[]) => void,
    currentUserCart?: CartItem[],
) => {
    const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);
    switch (action.toLowerCase()) {
        case CART_ERROR_CTA.CLEAR_CART:
            updateGroupCart(userId, []);
            break;
        case CART_ERROR_CTA.VIEW_SIMILAR_RESTAURANTS:
        case CART_ERROR_CTA.GO_TO_HOME:
        case CART_ERROR_CTA.GOTO_SIMILAR_RESTAURANTS:
            navigateToHomePage();
            break;

        case CART_ERROR_CTA.REMOVE_UNAVAILABLE_ITEMS: {
            const availableItems = getAvailableCartItems(currentUserCart || []);
            if (availableItems.length < 1 && isHost) {
                updateGroupCart(userId, []);
            } else {
                updateGroupCart(userId, availableItems);
            }
            break;
        }

        case CART_ERROR_CTA.RETRY:
        default:
            updateGroupCart(userId, currentUserCart || []);
            break;
    }
};
